/**
 * Share Utilities
 * Social media sharing and Web Share API utilities
 */

const { FRIEND_PAY_SHARE_URLS, FRIEND_PAY_MESSAGES } = require('../constant/friendPayConstants');
const { copyToClipboard } = require('./clipboard');

/**
 * Share handlers for different platforms
 * Each handler takes a message and URL and opens the appropriate sharing interface
 *
 * @param {string} shareType - Platform type (whatsapp, messenger, messages, email, facebook, share, copy)
 * @param {Object} options - Share options
 * @param {string} options.url - URL to share
 * @param {string} options.message - Message to share
 * @param {string} options.title - Title for the share (used in Web Share API)
 * @returns {void}
 */
exports.handleShare = function(shareType, options = {}) {
  const {
    url = window.location.href,
    message = '',
    title = FRIEND_PAY_MESSAGES.PAYMENT_REQUEST_TITLE
  } = options;

  const fullMessage = message ? `${message}\n\n${url}` : url;

  const shareHandlers = {
    /**
     * WhatsApp sharing - Opens WhatsApp with pre-filled message
     * Format: whatsapp://send?text=MESSAGE
     */
    whatsapp: () => {
      window.open(
        `${FRIEND_PAY_SHARE_URLS.WHATSAPP}${encodeURIComponent(fullMessage)}`,
        '_blank'
      );
    },

    /**
     * Facebook Messenger sharing - Opens Messenger with link
     * Format: fb-messenger://share?link=URL
     */
    messenger: () => {
      window.open(
        `${FRIEND_PAY_SHARE_URLS.MESSENGER}${encodeURIComponent(url)}`,
        '_blank'
      );
    },

    /**
     * SMS/Messages sharing - Opens default SMS app with pre-filled message
     * Format: sms:?&body=MESSAGE
     */
    messages: () => {
      window.open(
        `${FRIEND_PAY_SHARE_URLS.SMS}${encodeURIComponent(fullMessage)}`,
        '_blank'
      );
    },

    /**
     * Email sharing - Opens default email client with pre-filled subject and body
     * Format: mailto:?subject=SUBJECT&body=MESSAGE
     */
    email: () => {
      window.location.href = `${FRIEND_PAY_SHARE_URLS.EMAIL_MAILTO}${FRIEND_PAY_MESSAGES.EMAIL_SUBJECT}${FRIEND_PAY_SHARE_URLS.EMAIL_BODY}${encodeURIComponent(fullMessage)}`;
    },

    /**
     * Facebook sharing - Opens Facebook share dialog
     * Format: https://www.facebook.com/sharer/sharer.php?u=URL
     */
    facebook: () => {
      window.open(
        `${FRIEND_PAY_SHARE_URLS.FACEBOOK}${encodeURIComponent(url)}`,
        '_blank'
      );
    },

    /**
     * Web Share API - Uses native share sheet if available
     * Falls back to clipboard copy if not supported
     *
     * Browser support:
     * - Mobile: iOS Safari 12.2+, Chrome Android 61+, Samsung Internet
     * - Desktop: Chrome 89+, Edge 93+, Safari 14+ (macOS)
     */
    share: () => {
      if (navigator.share) {
        navigator.share({
          title: title,
          text: message,
          url: url
        })
        .then(() => {
          // Share was successful
        })
        .catch((error) => {
          // User cancelled or share failed
          if (error.name !== 'AbortError') {
            // If not cancelled by user, fall back to copy
            copyToClipboard(url);
          }
        });
      } else {
        // Fallback for browsers that don't support Web Share API
        copyToClipboard(url);
      }
    },

    /**
     * Copy to clipboard - Copies the URL to clipboard
     */
    copy: () => {
      copyToClipboard(url);
    }
  };

  const handler = shareHandlers[shareType];
  if (handler) {
    handler();
  } else {
    console.warn(`Unknown share type: ${shareType}`);
  }
};

/**
 * Check if Web Share API is supported
 * @returns {boolean} True if Web Share API is available
 */
exports.isWebShareSupported = function() {
  return navigator.share !== undefined;
};

/**
 * Build share URL for specific platform
 * Useful for generating share links without immediately opening them
 *
 * @param {string} platform - Platform type (whatsapp, messenger, facebook, etc.)
 * @param {Object} options - Share options
 * @param {string} options.url - URL to share
 * @param {string} options.message - Message to share
 * @returns {string|null} Share URL or null if platform doesn't use URLs
 */
exports.buildShareUrl = function(platform, options = {}) {
  const {
    url = window.location.href,
    message = ''
  } = options;

  const fullMessage = message ? `${message}\n\n${url}` : url;

  const urlBuilders = {
    whatsapp: () => `${FRIEND_PAY_SHARE_URLS.WHATSAPP}${encodeURIComponent(fullMessage)}`,
    messenger: () => `${FRIEND_PAY_SHARE_URLS.MESSENGER}${encodeURIComponent(url)}`,
    facebook: () => `${FRIEND_PAY_SHARE_URLS.FACEBOOK}${encodeURIComponent(url)}`,
    messages: () => `${FRIEND_PAY_SHARE_URLS.SMS}${encodeURIComponent(fullMessage)}`,
    email: () => `${FRIEND_PAY_SHARE_URLS.EMAIL_MAILTO}${FRIEND_PAY_MESSAGES.EMAIL_SUBJECT}${FRIEND_PAY_SHARE_URLS.EMAIL_BODY}${encodeURIComponent(fullMessage)}`
  };

  return urlBuilders[platform] ? urlBuilders[platform]() : null;
};
